//#region Imports Packages and Libs

import DOMPurify from "dompurify";
import { Util } from "../util";

//#endregion

//#region Sanitize Functions

/**
 * Sanitizes input to remove potential security vulnerabilities.
 *
 * @param input String to sanitize.
 * @param [options] DOMPurify's configuration object.
 * @returns Sanitized string.
 *
 * @example
 * sanitizeHtml("Hello world"); // Returns "Hello world"
 * sanitizeHtml("<img src=x onerror=alert(1)>"); // Returns "<img src='x'>"
 * sanitizeHtml("<script>alert(1)</script>"); // Returns ""
 * sanitizeHtml("<p>Hello <strong>world</strong></p>"); // Returns "<p>Hello <strong>world</strong></p>"
 */
export const sanitizeHtml = (input: string, options?: DOMPurify.Config): string => {
  try {
    // Check if input is valid
    if (!input || typeof input !== "string" || input.length === 0) return "";

    // Sanitize the input
    const out = DOMPurify.sanitize(input, options);
    return typeof out === "string" ? out : String(out);
  } catch (ex) {
    //console.error("[sanitize.ts] An error has occurred \n-> Inside sanitize -> Error: ", ex);
    return "";
  }
};

/**
 * Sanitizes HTML input to plain text by removing all HTML tags and entities.
 *
 * @param input - The string containing HTML to be sanitized
 * @returns A plain text string with all HTML removed, or an empty string if an error occurs
 *
 * @example
 * sanitizeToText("Hello world"); // Returns "Hello world"
 * sanitizeToText("<p>Hello <strong>world</strong></p>"); // Returns "Hello world"
 * sanitizeToText("<img src=x onerror=alert(1)>"); // Returns "" (empty string)
 * sanitizeToText("<script>alert(1)</script>"); // Returns ""
 */
export const sanitizeToText = (input: string): string => {
  try {
    // Sanitize the HTML to remove potentially harmful content and HTML tags also
    const safePlain = sanitizeHtml(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    if (!safePlain || typeof safePlain !== "string" || safePlain.length === 0) return "";

    // Trim whitespace from the result
    const trimmedString = Util.trimString(safePlain);
    return trimmedString;
  } catch (ex) {
    //console.error("[sanitize.ts] An error has occurred \n-> Inside sanitizeToText -> Error: ", ex);
    return "";
  }
};

/**
 * Removes common HTML event handler attributes (such as `onclick`, `onmouseover`, etc.)
 * from the provided HTML string to stop showing them in the UI chat bubble.
 *
 * @param input - The HTML string to sanitize by removing event handler attributes.
 * @returns The sanitized HTML string with event handler attributes removed.
 *
 * @example
 * sanitizeEventHandlers("Hello world"); // Returns "Hello world"
 * sanitizeEventHandlers("<div onclick=\"alert(1)\">Hello</div>"); // Returns "<div>Hello</div>"
 * sanitizeEventHandlers("<img src=x onerror=alert(1)>"); // Returns "<img src=x>"
 */
export const sanitizeEventHandlers = (input: string): string => {
  try {
    // Check if input is valid
    if (!input || typeof input !== "string" || input.length === 0) return "";

    // Check if there are any HTML tags
    // Matches: <div ...>, </p>, <img/>, <a href="..."> etc.
    const hasTag = /<\/?[a-zA-Z][\w:-]*(?:\s[^<>]*?)?>/m.test(input);
    if (!hasTag) return input; // skip cleaning on plain text like "2 < 5 > 3" or "onload=foo"

    // Define common event attributes to remove
    const COMMON_EVENT_ATTRS = [
      "onclick","ondblclick","onmousedown","onmouseup","onmouseover","onmousemove","onmouseenter",
      "onmouseleave","onwheel","oncontextmenu","onkeydown","onkeypress","onkeyup",
      "onchange","oninput","onsubmit","onreset","onfocus","onblur","onload","onerror"
    ];

    let cleaned = input;

    // Create a single regex that matches all event handlers
    const eventPattern = new RegExp(`\\s+(${COMMON_EVENT_ATTRS.join("|")})\\s*=\\s*(?:"[^"]*"|'[^']*'|[^\\s>]+)`, "gi");

    // Remove all event handlers, and replace ONLY inside tags
    cleaned = cleaned.replace(/<[^>]*>/g, (tag) => tag.replace(eventPattern, ""));
    return cleaned;
  } catch (ex) {
    //console.error("[sanitize.ts] An error has occurred \n-> Inside cleanEventHandlers -> Error: ", ex);
    return input;
  }
};

//#endregion
